import {gql} from "@apollo/client";

import {NewResourceResult} from "@/lib/queries/common.ts";
import {ServiceGroup, ServiceTag} from "@/lib/types/checks.ts";
import {GenericResponse, GraphQLResponse, ResponseStatus, ServerErrors} from "@/lib/types/common.ts";
import {
  Component,
  Incident,
  Metric,
  StatusPage,
  StatusPageBrandLookup,
  Subscriber,
  SubscriptionType,
} from "@/lib/types/statuspages.ts";

export const GQL_STATUS_PAGES_QUERY = gql`
  query StatusPages(
    $pageType: StatusPageType
    $orderBy: String
    $search: String
    $limit: Int
    $offset: Int
    $filters: StatusPageFilters
    $withIncidentCount: Boolean
  ) {
    statusPages(
      pageType: $pageType
      limit: $limit
      offset: $offset
      orderBy: $orderBy
      search: $search
      filters: $filters
      withIncidentCount: $withIncidentCount
    ) {
      items {
        id
        name
        page_type
        private_url
        public_url
        cname_url
        subscribers_count
      }
      total
    }
  }
`;

export const GQL_STATUS_PAGES_QUERY_WITH_INCIDENT_COUNTS = gql`
  query StatusPages(
    $pageType: StatusPageType
    $orderBy: String
    $search: String
    $limit: Int
    $offset: Int
    $filters: StatusPageFilters
  ) {
    statusPages(
      pageType: $pageType
      limit: $limit
      offset: $offset
      orderBy: $orderBy
      search: $search
      filters: $filters
      withIncidentCount: true
    ) {
      items {
        id
        name
        page_type
        theme
        private_url
        public_url
        cname_url
        is_served_by_spa
        subscribers_count
        visibility_level
        active_maintenance_count
        active_incidents_count
        scheduled_maintenance_count
      }
      total
    }
  }
`;

export const GQL_STATUS_PAGES_NAMES_QUERY = gql`
  query StatusPages($search: String, $limit: Int, $offset: Int, $filters: StatusPageFilters) {
    statusPages(limit: $limit, offset: $offset, search: $search, filters: $filters, withIncidentCount: false) {
      items {
        id
        name
      }
      total
    }
  }
`;

export const GQL_STATUS_PAGE_QUERY = gql`
  query StatusPage($id: Int!) {
    statusPage(id: $id) {
      id
      name
      slug
      cname
      description
      page_type
      public_url
      private_url
      cname_url
      is_served_by_spa
      default_status_date_range
      default_history_date_range
      max_visible_days
      max_visible_component_days
      allow_subscriptions
      allow_subscriptions_email
      allow_subscriptions_rss
      allow_subscriptions_slack
      allow_subscriptions_sms
      allow_subscriptions_webhook
      allow_notifications
      allow_search_indexing
      allow_drill_down
      visibility_level
      timezone
      used_timezone
      time_format
      show_status_tab
      show_active_incidents
      show_component_response_time
      show_history_tab
      uptime_calculation_type
      allow_pdf_report
      show_history_snake
      show_summary_metrics
      show_component_history
      show_past_incidents
      hide_empty_tabs_status
      hide_empty_tabs_history
      layout_preset
      show_component_bars
      show_component_descriptions
      show_overall_status
      show_branding_footer
      auth_username
      auth_password

      logo_url
      favicon_url
      google_analytics_code
      contact_email
      custom_css
      custom_header_html
      custom_footer_html
      custom_css_inspire
      custom_header_html_inspire
      custom_footer_html_inspire
      theme
      custom_header_text_color_hex
      custom_header_bg_color_hex
      email_logo_url
      email_from
      email_reply_to
      company_website_url
      subscribers_count
      external_users_count

      active_incidents {
        id
        name
        duration
        starts_at
        ends_at
        incident_type
        include_in_global_metrics
        notify_subscribers
        update_component_status
        send_maintenance_start_notification
        updates {
          id
          updated_at
          is_modified
          description
          description_html
          incident_state
          incident_state_display
        }
        affected_components {
          id
          obj_id
          name
          status
          status_display
          description
        }
      }
      upcoming_maintenance {
        id
        name
        duration
        starts_at
        ends_at
        incident_type
        include_in_global_metrics
        notify_subscribers
        update_component_status
        send_maintenance_start_notification
        updates {
          id
          updated_at
          is_modified
          description
          description_html
          incident_state
          incident_state_display
        }
        affected_components {
          id
          name
          obj_id
          status
          status_display
          description
        }
      }
      components {
        id
        name
        description
        status
        service
        service_name
        service_type
        auto_status_up
        auto_status_down
        is_group
        group
        subcomponents {
          id
          name
          description
          group
          is_group
          status
          service
          service_name
          service_type
          auto_status_up
          auto_status_down
        }
      }
      metrics {
        id
        name
        source
        service
        service_name
        service_type
        is_visible
      }
      samlSettings {
        sp_uuid
        idp_entity_id
        idp_sso_target_url
        idp_certificate
        sp_entity_id
        sp_acs_url
        sp_wayfless_url
        use_sha1_for_signature
        unsigned_authn_requests
      }
    }
    componentStatuses {
      value
      display
      rank
      color
      is_operational
      is_failure
    }
    testIDPs {
      items {
        name
        info
        idp_entity_id
        idp_sso_target_url
        idp_certificate
      }
    }
  }
`;

export interface UpdateStatusPageResult {
  updateStatusPage: NewResourceResult;
}

export const GQL_UPDATE_STATUS_PAGE = gql`
  mutation UpdateStatusPage($id: Int!, $data: UpdateStatusPageInput!) {
    updateStatusPage(id: $id, data: $data) {
      id
      success
      errors
    }
  }
`;

export type UpdateStatusPageV2Result = GraphQLResponse<StatusPage>;

export const GQL_UPDATE_STATUS_PAGE_V2 = gql`
  mutation UpdateStatusPageV2($id: Int!, $data: UpdateStatusPageInput!) {
    response: updateStatusPageV2(id: $id, data: $data) {
      ... on StatusPageMutationSuccess {
        status
        data {
          id
        }
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface NewStatusPageResult {
  newStatusPage: NewResourceResult;
}

export const GQL_NEW_STATUS_PAGE = gql`
  mutation NewStatusPage($pageType: StatusPageType!, $data: NewStatusPageInput!) {
    newStatusPage(pageType: $pageType, data: $data) {
      id
      success
      errors
    }
  }
`;

export type CreateStatusPageResult = GraphQLResponse<StatusPage>;

export const GQL_CREATE_STATUS_PAGE = gql`
  mutation CreateStatusPage($data: CreateStatusPageInput!) {
    response: createStatusPage(data: $data) {
      ... on StatusPageMutationSuccess {
        status
        data {
          id
        }
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface UpdateComponentResult {
  updateComponent: GenericResponse;
}

export const GQL_UPDATE_COMPONENT = gql`
  mutation UpdateComponent($statusPageID: Int!, $data: UpdateComponentInput!) {
    updateComponent(statusPageID: $statusPageID, data: $data) {
      success
      errors
    }
  }
`;

export interface UpdateMetricResult {
  updateMetric: GenericResponse;
}

export const GQL_UPDATE_METRIC = gql`
  mutation UpdateMetric($statusPageID: Int!, $data: UpdateMetricInput!) {
    updateMetric(statusPageID: $statusPageID, data: $data) {
      success
      errors
    }
  }
`;

export const GQL_STATUS_PAGE_BRAND_LOOKUP = gql`
  query StatusPageBrandLookup($url: String!) {
    statusPageBrandLookup(url: $url) {
      outcome
      logo_base64
      logo_filename
      logo_content_type
      favicon_base64
      favicon_filename
      favicon_content_type
      colors {
        hex
        type
      }
      suggested_header_bg_color_hex
      suggested_header_text_color_hex
    }
  }
`;

export interface StatusPageBrandLookupResult {
  statusPageBrandLookup: StatusPageBrandLookup | null;
}

export interface DeleteStatusPageResult {
  deleteStatusPage: GenericResponse;
}

export interface DuplicateStatusPageResult {
  duplicateStatusPage: NewResourceResult;
}

export const GQL_DELETE_STATUS_PAGE = gql`
  mutation DeleteStatusPage($id: Int!) {
    deleteStatusPage(id: $id) {
      success
      errors
    }
  }
`;

export const GQL_DUPLICATE_STATUS_PAGE = gql`
  mutation DuplicateStatusPage($id: Int!) {
    duplicateStatusPage(id: $id) {
      id
      success
      errors
    }
  }
`;

export interface GetServiceTypesAndTagsResult {
  monitoringServiceTypes: ServiceGroup[];
  tags: ServiceTag[];
}

export const GQL_GET_SERVICE_TYPES_AND_TAGS = gql`
  query GetServiceTypesAndTags {
    monitoringServiceTypes {
      ...MonitoringGroupFields
    }
    tags {
      id
      tag
      color
      color_hex
    }
  }
`;

export interface CheckInfo {
  id: number;
  name: string;
  address: string;
  /** Human-readable, e.g. "HTTP(S)". Not a key — use monitoring_service_type for that. */
  type: string;
  monitoring_service_type?: string;
}

export interface FindChecksResult {
  findChecks: {
    items: CheckInfo[];
  };
}

export const GQL_FIND_CHECKS = gql`
  query FindChecks(
    $search: String!
    $limit: Int!
    $serviceType: String
    $excludeServiceType: String
    $tags: [Int!]
    $tagsMatch: MatchMode
    $include: [Int!]
  ) {
    findChecks(
      search: $search
      limit: $limit
      serviceType: $serviceType
      excludeServiceType: $excludeServiceType
      tags: $tags
      tags_match: $tagsMatch
      include: $include
    ) {
      items {
        id
        name
        address
        type
        monitoring_service_type
      }
    }
  }
`;

export interface WidgetEntity {
  id: number;
  name: string;
  type: string;
  widget_url: string;
  public_details_url: string;
}

export interface FindCheckOrStatusPageResult {
  items: [WidgetEntity];
}

export const GQL_FIND_CHECK_OR_STATUS_PAGE = gql`
  query FindCheckOrStatusPage($search: String!, $limit: Int!) {
    items: findCheckOrStatusPage(search: $search, limit: $limit) {
      id
      name
      type
      widget_url
      public_details_url
    }
  }
`;

export interface ComponentOrder {
  id: number;
  sortingWeight: number;
  groupId?: number;
}

export const GQL_UPDATE_COMPONENTS_ORDER = gql`
  mutation UpdateComponentsOrder($statusPageID: Int!, $data: [ComponentOrder!]!) {
    updateComponentsOrder(statusPageID: $statusPageID, data: $data) {
      success
      errors
    }
  }
`;

export interface MetricOrder {
  metricId: number;
  sortingWeight: number;
}

export const GQL_UPDATE_METRICS_ORDER = gql`
  mutation UpdateMetricsOrder($statusPageID: Int!, $data: [MetricOrder!]!) {
    updateMetricsOrder(statusPageID: $statusPageID, data: $data) {
      success
      errors
    }
  }
`;

export interface DeleteComponentResponse {
  deleteComponent: GenericResponse;
}

export const GQL_DELETE_COMPONENT = gql`
  mutation DeleteComponent($statusPageID: Int!, $componentID: Int!) {
    deleteComponent(statusPageID: $statusPageID, componentID: $componentID) {
      success
      errors
    }
  }
`;

export const GQL_BULK_DELETE_COMPONENTS = gql`
  mutation DeleteStatusPageComponents($statusPageID: Int!, $componentIDs: [Int!]!) {
    bulkDeleteStatusPageComponents(statusPageID: $statusPageID, componentIDs: $componentIDs) {
      success
      errors
    }
  }
`;

export interface DeleteMetricResponse {
  deleteMetric: GenericResponse;
}

export const GQL_DELETE_METRIC = gql`
  mutation DeleteMetric($statusPageID: Int!, $metricID: Int!) {
    deleteMetric(statusPageID: $statusPageID, metricID: $metricID) {
      success
      errors
    }
  }
`;

export const GQL_BULK_DELETE_METRICS = gql`
  mutation BulkDeleteMetrics($statusPageID: Int!, $metricIDs: [Int!]!) {
    bulkDeleteMetrics(statusPageID: $statusPageID, metricIDs: $metricIDs) {
      success
      errors
    }
  }
`;

export interface DeleteIncidentResponse {
  deleteIncident: GenericResponse;
}

export const GQL_DELETE_INCIDENT = gql`
  mutation DeleteIncident($statusPageID: Int!, $incidentID: Int!) {
    deleteIncident(statusPageID: $statusPageID, incidentID: $incidentID) {
      success
      errors
    }
  }
`;

export type StatusPageIncidentsResponse = GraphQLResponse<Incident[]>;

export const GQL_STATUSPAGE_INCIDENTS = gql`
  query GetIncidents(
    $id: Int!
    $offset: Int!
    $limit: Int!
    $incidentType: IncidentType
    $incidentTime: IncidentTime
    $query: String
    $sortBy: String
  ) {
    response: statusPageIncidents(
      id: $id
      offset: $offset
      limit: $limit
      incidentType: $incidentType
      incidentTime: $incidentTime
      query: $query
      sortBy: $sortBy
    ) {
      ... on ErrorResponse {
        status
        message
      }
      ... on StatusPageIncidents {
        status
        data {
          id
          name
          starts_at
          ends_at
          duration
          incident_type
          incident_state
          include_in_global_metrics
          notify_subscribers
          update_component_status
          send_maintenance_start_notification
          updates {
            id
            updated_at
            is_modified
            description
            description_html
            incident_state
            incident_state_display
          }
          affected_components {
            id
            obj_id
            name
            description
            status
            status_display
          }
        }
        total
      }
    }
  }
`;

export const GQL_INCIDENTS_COUNTS = gql`
  query IncidentCounts($id: Int!, $incidentType: IncidentType) {
    ACTIVE: statusPageIncidents(id: $id, incidentType: $incidentType, incidentTime: ACTIVE, limit: 1, offset: 0) {
      ... on StatusPageIncidents {
        total
      }
    }
    RESOLVED: statusPageIncidents(id: $id, incidentType: $incidentType, incidentTime: RESOLVED, limit: 1, offset: 0) {
      ... on StatusPageIncidents {
        total
      }
    }
    UPCOMING: statusPageIncidents(id: $id, incidentType: $incidentType, incidentTime: UPCOMING, limit: 1, offset: 0) {
      ... on StatusPageIncidents {
        total
      }
    }
  }
`;

export interface UpdateIncidentResult {
  updateIncident: GenericResponse;
}

export const GQL_UPDATE_INCIDENT = gql`
  mutation UpdateIncident($statusPageID: Int!, $data: UpdateIncidentInput!) {
    updateIncident(statusPageID: $statusPageID, data: $data) {
      success
      errors
    }
  }
`;

export const GQL_BULK_DELETE_INCIDENTS = gql`
  mutation BulkDeleteIncidents($incidentIds: [Int!]!) {
    response: bulkDeleteIncidents(incidentIds: $incidentIds) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface SubscribersPageResult {
  subscribers: {
    items: Subscriber[];
    total: number;
  };
}

export type SubscriberCountResults = Record<SubscriptionType, Record<"total", number>>;

export const GQL_SUBSCRIBERS_COUNTS = gql`
  query SubscriberCounts($statusPageID: Int!) {
    EMAIL: subscribers(statusPageID: $statusPageID, query: "", subscriptionType: EMAIL, limit: 1, offset: 0) {
      total
    }
    SMS: subscribers(statusPageID: $statusPageID, query: "", subscriptionType: SMS, limit: 1, offset: 0) {
      total
    }
    SLACK: subscribers(statusPageID: $statusPageID, query: "", subscriptionType: SLACK, limit: 1, offset: 0) {
      total
    }
    WEBHOOK: subscribers(statusPageID: $statusPageID, query: "", subscriptionType: WEBHOOK, limit: 1, offset: 0) {
      total
    }
  }
`;

export const GQL_SUBSCRIBERS = gql`
  query Subscribers(
    $statusPageID: Int!
    $offset: Int!
    $query: String!
    $sort: [String!]
    $limit: Int!
    $subscriptionType: SubscriptionType!
  ) {
    subscribers(
      statusPageID: $statusPageID
      query: $query
      sort: $sort
      offset: $offset
      limit: $limit
      subscriptionType: $subscriptionType
    ) {
      items {
        id
        type
        target
        status
        created_at
        slack_integration {
          integration_status
          workspace_name
          channel_name
        }
      }
      total
    }
  }
`;

export interface UnsubscribeResult {
  unsubscribeTarget: GenericResponse;
}

export const GQL_UNSUBSCRIBE_TARGET = gql`
  mutation UnsubscribeTarget($statusPageID: Int!, $targets: [Int!]!) {
    response: unsubscribeTarget(statusPageID: $statusPageID, targets: $targets) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface ImportSubscribersResult {
  importSubscribers: GenericResponse;
}

export const GQL_IMPORT_SUBSCRIBERS = gql`
  mutation ImportSubscribers($statusPageID: Int!, $file: Upload!) {
    importSubscribers(statusPageID: $statusPageID, file: $file) {
      success
      errors
    }
  }
`;

export const GQL_REMOVE_SAML_SETTINGS = gql`
  mutation RemoveSAMLSettings($statusPageID: Int!) {
    response: removeSAMLSettings(statusPageID: $statusPageID) {
      status
    }
  }
`;

export const GQL_UPDATE_SAML_SETTINGS = gql`
  mutation UpdateSAMLSettings($statusPageID: Int!, $data: SAMLSettingsInput!) {
    response: updateSAMLSettings(statusPageID: $statusPageID, data: $data) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface UpdateSubscriptionEmailDomainResult {
  response: {
    status: ResponseStatus;
    message: ServerErrors;
  };
}

export const GQL_SUBSCRIPTION_EMAIL_DOMAINS = gql`
  query SubscriptionEmailDomains($statusPageID: Int!, $query: String, $emailDomainType: SubscriptionEmailDomainType!) {
    subscriptionEmailDomains(statusPageID: $statusPageID, query: $query, emailDomainType: $emailDomainType) {
      items {
        id
        domain
        created_at
      }
      total
    }
  }
`;

export const GQL_SUBSCRIPTION_EMAIL_DOMAINS_COUNTS = gql`
  query SubscriptionEmailDomainsAll($statusPageID: Int!, $query: String) {
    allowed: subscriptionEmailDomains(statusPageID: $statusPageID, query: $query, emailDomainType: ALLOWED) {
      total
    }
    blocked: subscriptionEmailDomains(statusPageID: $statusPageID, query: $query, emailDomainType: BLOCKED) {
      total
    }
  }
`;

export const GQL_CREATE_SUBSCRIPTION_EMAIL_DOMAIN = gql`
  mutation CreateSubscriptionEmailDomain($statusPageID: Int!, $data: SubscriptionEmailDomainInput!) {
    response: createSubscriptionEmailDomain(statusPageID: $statusPageID, data: $data) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_DELETE_SUBSCRIPTION_EMAIL_DOMAINS = gql`
  mutation DeleteSubscriptionEmailDomains($statusPageID: Int!, $domainIds: [Int!]!) {
    response: deleteSubscriptionEmailDomains(statusPageID: $statusPageID, domainIds: $domainIds) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface IncidentsListStats {
  active: number;
  identified: number;
  investigating: number;
  maintenance: number;
  monitoring: number;
  resolved: number;
}

export interface IncidentsListResponseValues {
  status: ResponseStatus;
  stats: IncidentsListStats;
  total: number;
  data: Incident[];
}

export interface IncidentsListResponse {
  response: IncidentsListResponseValues;
}

export const GQL_INCIDENTS = gql`
  query Incidents(
    $offset: Int
    $limit: Int
    $sort: String
    $search: String
    $filters: IncidentsFilters
    $visible_range: IncidentsFiltersRange
  ) {
    response: incidents(
      offset: $offset
      limit: $limit
      sort: $sort
      search: $search
      filters: $filters
      visible_range: $visible_range
    ) {
      stats {
        active
        identified
        investigating
        maintenance
        monitoring
        resolved
      }
      total
      data {
        id
        name
        statuspage {
          id
          name
          public_url
        }
        latest_update_incident_state
        latest_update_description
        starts_at
        ends_at
        incident_type
      }
    }
  }
`;

export interface IncidentResponse {
  incident: Incident;
}

export const GQL_INCIDENT = gql`
  query Incident($id: Int!) {
    incident(id: $id) {
      id
      name
      starts_at
      ends_at
      incident_type
      incident_state
      include_in_global_metrics
      notify_subscribers
      update_component_status
      send_maintenance_start_notification
      updates {
        id
        updated_at
        is_modified
        description
        description_html
        incident_state
        incident_state_display
      }
      affected_components {
        id
        obj_id
        name
        description
        status
        status_display
      }
      statuspage {
        id
      }
    }
  }
`;

export const GQL_INCIDENTS_STATUS_PAGE_FILTER = gql`
  query StatusPages($search: String, $limit: Int) {
    values: statusPages(search: $search, limit: $limit) {
      total
      items {
        key: id
        display: name
      }
    }
  }
`;

export interface StatusPageComponentsListResponse {
  response: {
    total: number;
    items: Component[];
  };
}

export const GQL_STATUSPAGE_COMPONENTS = gql`
  query StatusPageComponents(
    $statusPageId: Int!
    $search: String
    $excludeComponentsInGroups: Boolean
    $excludeGroups: Boolean
  ) {
    response: statusPageComponents(
      statusPageID: $statusPageId
      search: $search
      excludeComponentsInGroups: $excludeComponentsInGroups
      excludeGroups: $excludeGroups
    ) {
      total
      items {
        id
        name
        description
        status
        service
        service_name
        service_type
        auto_status_up
        auto_status_down
        is_group
        group
        sorting_weight
        just_added
        cached_response_time
        subcomponents {
          id
          name
          description
          group
          is_group
          status
          service
          service_name
          service_type
          auto_status_up
          auto_status_down
          sorting_weight
          just_added
          cached_response_time
        }
      }
    }
  }
`;

export interface StatusPageSystemMetricsListResponse {
  response: {
    total: number;
    items: Metric[];
  };
}

export const GQL_STATUSPAGE_SYSTEM_METRICS = gql`
  query StatusPageSystemMetrics($statusPageId: Int!, $search: String) {
    response: statusPageSystemMetrics(statusPageID: $statusPageId, search: $search) {
      total
      items {
        id
        name
        source
        service
        service_name
        service_type
        is_visible
      }
    }
  }
`;
